import { CancellationToken } from "../../../base/common/cancellation";
import { fuzzyScore } from "../../../base/common/filters";
import { FuzzyScorerCache, prepareQuery, scoreFuzzy2, scoreItemFuzzy } from "../../../base/common/fuzzyScorer";
import { DisposableStore } from "../../../base/common/lifecycle";
import { fuzzyContains } from "../../../base/common/strings";
import { IAssetsService } from "../../../editor/services/assets/common/assets";
import { IAnalyticsService } from "../../analytics/common/analytics";
import { ICommandService } from "../../commands/common/commands";
import { IHierarchyService, NodeFlags } from "../../hierarchy/common/hierarchy";
import { ILogService } from "../../log/common/log";
import { isPropertyLike } from "../../properties/common/properties";
import { SetPropertyCommand } from "../../properties/common/propertyCommands";
import { FastAndSlowPicks, IPickerQuickAccessItem, PickerQuickAccessProvider, Picks } from "../../quickinput/browser/pickerAccessProvider";
import { IQuickAccessProviderRunOptions } from "../../quickinput/common/quickAccess";
import { IQuickPickItemWithResource, IQuickPickSeparator, quickPickItemScorerAccessor } from "../../quickinput/common/quickInput";
import { IResourceService } from "../../resources/common/resources";
import { IStorageService, StorageScope, StorageTarget } from "../../storage/common/storage";
import { SetMaterialCommand } from "../../webgl/three.js/commands/materialCommands";
import { isMaterial, isMeshLike, isSkinnedMeshLike } from "../../webgl/three.js/helper";
import { isNodeMaterial } from "../../webgl/three.js/helper.nodes";
import { fuzzyScoreSortingHelper } from "./helper";

interface IPropertyPickItem extends IQuickPickItemWithResource, IPickerQuickAccessItem {
    minScore?: number;
    sortOffset?: number;
}

type PropertyPickerItem = IPropertyPickItem | IQuickPickSeparator;

export class PropertyPicker extends PickerQuickAccessProvider<IPropertyPickItem> {

    static readonly PREFIX = "@property ";
    static readonly MATERIAL_PREFIX = "@property material ";
    static readonly TEXTURE_PREFIX = "@property texture ";
    static readonly HDRI_PREFIX = "@property hdri ";

    private readonly fuzzyCache: FuzzyScorerCache = {};

    private static readonly NoResultItem: IPropertyPickItem = {
        id: `no-results`,
        label: `No results found`,
    }

    constructor(
        @ICommandService private readonly commandService: ICommandService,
        @IResourceService private readonly resourceCacheService: IResourceService,
        @IHierarchyService private readonly hierarchyService: IHierarchyService,
        @IAnalyticsService private readonly analyticsService: IAnalyticsService,
        @IAssetsService private readonly assetsService: IAssetsService,
        @IStorageService private readonly storageService: IStorageService,
        @ILogService logService: ILogService,
    ) {
        super(PropertyPicker.PREFIX);
    }

    private getStorageKey(filterPrefix: string): string {
        // Create separate storage keys for each filter type
        if (filterPrefix === "material") return "propertypicker.lastpicks.material";
        if (filterPrefix === "hdri" || filterPrefix === "hdr") return "propertypicker.lastpicks.hdri";
        if (filterPrefix === "texture") return "propertypicker.lastpicks.texture";
        return "propertypicker.lastpicks.default";
    }

    private addToLastPicks(item: IPropertyPickItem, filterPrefix: string) {
        if (!item?.id) return;
        const storageKey = this.getStorageKey(filterPrefix);
        const lastpicks = this.storageService.getObject<string[]>(storageKey, StorageScope.WORKSPACE, []).filter(e => typeof e === "string");
        const existingIndex = lastpicks.findIndex(pick => pick === item.id);
        if (existingIndex !== -1) {
            lastpicks.splice(existingIndex, 1);
        }
        lastpicks.unshift(item.id);
        if (lastpicks.length > 20) {
            lastpicks.pop();
        }
        this.storageService.store(storageKey, lastpicks, StorageScope.WORKSPACE, StorageTarget.USER);
    }


    protected async _getPicks(filter: string, disposables: DisposableStore, token: CancellationToken, runOptions?: IQuickAccessProviderRunOptions): Promise<Picks<IPropertyPickItem>> {

        const results: IPropertyPickItem[] = [];

        const target = runOptions?.target;
        const property = isPropertyLike(target) ? target : undefined;
        if (!property) {
            throw new Error("PropertyPicker: target is not a property");
        }

        // Parse filter to extract asset type hints
        // Note: "@property " prefix is already removed by PickerQuickAccessProvider
        const filterLower = filter.toLowerCase().trim();

        const assetTypeHints = {
            material: filterLower.startsWith("material"),
            hdri: filterLower.startsWith("hdri") || filterLower.startsWith("hdr"),
            texture: filterLower.startsWith("texture"),
        };

        // Determine filter prefix for storage key
        let filterPrefix = "default";
        if (assetTypeHints.material) filterPrefix = "material";
        else if (assetTypeHints.hdri) filterPrefix = "hdri";
        else if (assetTypeHints.texture) filterPrefix = "texture";

        // Remove type keyword from the start of the search query
        const searchQuery = filter.replace(/^(material|hdri?|texture)\s*/i, "").trim();

        for (const node of this.hierarchyService.iterateNodes()) {

            if (token.isCancellationRequested) break;
            if (node.flags & NodeFlags.HideInHierarchy) continue;

            if (isMeshLike(node.owner) || isSkinnedMeshLike(node.owner)) {
                this.register(node.owner);
            }
        }

        // @TODO: use property accept metadata to narrow down resource types

        const query = prepareQuery(searchQuery);

        for (const materialData of this.resourceCacheService.iterateResources()) {
            if (token.isCancellationRequested) break;

            const ref = materialData.reference;

            if (isMaterial(ref)) {

                // We can not set the output color transform node material via picker
                if (isNodeMaterial(ref) && ref.name === "outputColorTransform") {
                    continue; // skip default node material
                }

                const label = ref.name || ref.type;
                let description = ref.type !== label ? ref.type : ref.type;
                if ((ref as any).wireframe === true) description += ", Wireframe";
                const id = `material.${ref.uuid}`;
                const item: IPropertyPickItem = {
                    id: id,
                    name: ref.name,
                    label: ref.name || (ref.type + "*"),
                    description: description,
                    icon: "material",
                    accept: (keymods) => {
                        this.analyticsService.logEvent("picker", {
                            action: "pick",
                            from: "property",
                            type: ref.type,
                            label: label
                        });

                        this.addToLastPicks(item, filterPrefix);

                        this.commandService.executeCommand<SetPropertyCommand>(SetPropertyCommand.ID, {
                            property: property,
                            value: ref,
                            metadata: {
                                source: "user",
                                target: property.key
                            }
                        });
                    }
                };
                results.push(item);
            }
        }

        if (token.isCancellationRequested) return [];

        // Determine which asset types to fetch based on the filter
        const assetTypesToFetch: Array<"material" | "hdr" | "texture"> = [];

        // If specific types are mentioned, only fetch those
        if (assetTypeHints.material || assetTypeHints.hdri || assetTypeHints.texture) {
            if (assetTypeHints.material) assetTypesToFetch.push("material");
            if (assetTypeHints.hdri) assetTypesToFetch.push("hdr");
            if (assetTypeHints.texture) assetTypesToFetch.push("texture");
        } else {
            // Default: fetch materials (existing behavior)
            assetTypesToFetch.push("material");
        }

        // Fetch assets for each type
        for (const assetType of assetTypesToFetch) {
            if (token.isCancellationRequested) break;

            const assets = await this.assetsService.list({ type: assetType }, token);
            for (const asset of assets.assets) {
                if (token.isCancellationRequested) break;
                const id = `asset.${asset.id}`;
                let description = asset.description && asset.description.length > 100 ? asset.description.substring(0, 100) + "..." : asset.description;
                if (asset.provider) description = asset.provider + (description ? ` – ${description}` : "");

                let tooltip: string | undefined = undefined;
                if (asset.thumbnailUrl) {
                    tooltip = `![thumbnail](${asset.thumbnailUrl})`;
                }

                // Update label based on asset type
                let label = asset.name || `Unnamed ${assetType}`;

                const item: IPropertyPickItem = {
                    id: id,
                    name: undefined,
                    label: label,
                    description: description,
                    icon: assetType === "hdr" ? "hdri" : "asset",
                    thumbnailUrl: asset.thumbnailUrl,
                    tooltip: tooltip,

                    // minScore: 120,
                    sortOffset: -1000000, // prefer local materials
                    accept: (keymods) => {
                        this.addToLastPicks(item, filterPrefix);
                        // @TODO: select does use current selection which is NOT what we want here
                        return this.assetsService.handleInteraction({
                            type: "targets",
                            assets: [asset],
                            targets: [property],
                        }, token)?.then(() => { })
                    }
                };
                results.push(item);
            }
        }

        // If there's no search query, show recent picks at the top
        if (!searchQuery || searchQuery.trim().length === 0) {
            const storageKey = this.getStorageKey(filterPrefix);
            const lastpicks = this.storageService.getObject<string[]>(storageKey, StorageScope.WORKSPACE, []).filter(e => typeof e === "string");

            if (lastpicks.length > 0) {
                // Find recent picks that exist in current results
                const recentItems = lastpicks
                    .map(pickId => results.find(item => item.id === pickId))
                    .filter((item): item is IPropertyPickItem => item !== undefined);

                if (recentItems.length > 0) {
                    // Get remaining items that weren't in recent picks
                    const recentIds = new Set(recentItems.map(item => item.id));
                    const remainingItems = results.filter(item => !recentIds.has(item.id));

                    // Sort remaining items
                    remainingItems.sort((a, b) => {
                        const score_a = (a.sortOffset || 0);
                        const score_b = (b.sortOffset || 0);
                        if (score_a !== score_b) {
                            return score_b - score_a; // Higher sortOffset first
                        }
                        return (a.label || "").localeCompare(b.label || "");
                    });

                    // Add separator between recent picks and other items (if there are remaining items)
                    if (remainingItems.length > 0) {
                        const separator: IQuickPickSeparator = {
                            type: 'separator'
                        };
                        // Return with separator: cast to any to work around type constraint
                        return [...recentItems, separator, ...remainingItems] as any;
                    }

                    // Only recent items, no separator needed
                    return recentItems;
                }
            }

            // No recent picks, return all sorted
            return results.sort((a, b) => {
                const score_a = (a.sortOffset || 0);
                const score_b = (b.sortOffset || 0);
                if (score_a !== score_b) {
                    return score_b - score_a; // Higher sortOffset first
                }
                return (a.label || "").localeCompare(b.label || "");
            });
        }

        const scoredResults = results
            .map(item => {
                const score = scoreItemFuzzy(item, query, true, quickPickItemScorerAccessor, this.fuzzyCache);
                return { item, score };
            })
            .filter(({ item, score }) => score.score > (item.minScore || 0))
            .sort((a, b) => {
                let score_a = a.score.score;
                let score_b = b.score.score;
                if (a.item.sortOffset) score_a += a.item.sortOffset;
                if (b.item.sortOffset) score_b += b.item.sortOffset;
                return fuzzyScoreSortingHelper(a.item.name, score_a, b.item.name, score_b);
            })
            .map(({ item, score }) => {
                item.label += " " + score.score;
                return item;
            });

        if (scoredResults.length <= 0) {
            return [PropertyPicker.NoResultItem];
        }
        return scoredResults;
    }

    private register(object: import("three").Mesh) {
        if (Array.isArray(object.material)) {
            for (const mat of object.material) {
                this.registerMaterial(mat);
            }
        }
        else {
            const mat = object.material;
            this.registerMaterial(mat);
        }
    }
    private registerMaterial(material: import("three").Material) {
        this.resourceCacheService.registerResource({
            reference: material,
        });
    }
}